import * as React from 'react';
import { isNewTextFieldEnabledForForm } from 'feature-flags';
import { isEmpty } from 'lodash';
import { checkSignupStatus } from 'rest/anonymous-email';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import { TextField } from '@atlassian/help-center-common-component/fields/text-field';
import type { Props as TextFieldProps } from '@atlassian/help-center-common-component/fields/text-field';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { isEmail } from '@atlassian/help-center-common-component/fields/user-picker/user-picker-helper';
import { AnonymousEmailSignupMessage } from './anonymous-email-signup-message';
import { ERROR_REASON_KEYS } from 'rest/request-create/types';

interface Props extends FieldFormProps<string>, WithAnalyticsEventsProps {
    defaultValue?: string;
    errorReasonKey?: ERROR_REASON_KEYS;
}

const REQUIRED = 'REQUIRED';

export const AnonymousEmailField = (props: Props & TextFieldProps) => {
    const {
        id,
        label,
        required,
        defaultValue,
        description,
        errorMessage,
        disabled,
        shouldFitContainer,
        placeholder,
        isLabelHidden,
        type,
        autoFocus,
        onFocus,
        onChange,
    } = props;

    const [showWarningMessage, setShowWarningMessage] = React.useState(false);
    const [email, setEmail] = React.useState(defaultValue || '');

    const onBlur = React.useCallback(() => {
        if (isEmail(email)) {
            // Disabling existing violations, should be fixed when revisited.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            checkSignupStatus(email)
                .map((response) => {
                    if (response.status === REQUIRED) {
                        setShowWarningMessage(true);
                    } else {
                        setShowWarningMessage(false);
                    }
                })
                .toPromise();
        }
    }, [email, setShowWarningMessage]);

    const onChangeCallback = React.useCallback(
        (name: string, value: string) => {
            if (onChange) {
                onChange(name, value);
            }
            setEmail(value);
        },
        [onChange]
    );

    const isSignupRequiredError = props.errorReasonKey === ERROR_REASON_KEYS.SIGNUP_REQUIRED;

    const isInvalid = !isEmpty(errorMessage);
    const inputType = type;
    const field = (
        <TextField
            id={id}
            label={''}
            isLabelHidden
            defaultValue={defaultValue}
            value={email}
            onChange={onChangeCallback}
            isInvalid={isInvalid}
            shouldFitContainer={shouldFitContainer}
            disabled={disabled}
            placeholder={placeholder}
            type={inputType}
            // Disabling existing violations, should be fixed when revisited.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            updatedTextField={isNewTextFieldEnabledForForm()}
        />
    );

    return (
        <FieldBase
            htmlFor={id}
            label={label}
            required={required}
            description={description}
            noLabel={isLabelHidden}
            errorMessage={isSignupRequiredError ? '' : errorMessage}
        >
            {field}
            {(showWarningMessage || isSignupRequiredError) && (
                <AnonymousEmailSignupMessage isError={isSignupRequiredError} />
            )}
        </FieldBase>
    );
};

AnonymousEmailField.defaultProps = {
    shouldFitContainer: true,
    type: 'text',
};

export default withAnalyticsContext({ componentName: 'AnonymousEmailField' })(
    withAnalyticsEvents()(AnonymousEmailField)
);
